import { useEffect, useRef, useState } from "react";
import {
  MapContainer,
  TileLayer,
  useMap,
  GeoJSON,
  Marker,
  Circle,
} from "react-leaflet";
import L from "leaflet";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Navigation, MapPin, Loader2, Award } from "lucide-react";
import { toast } from "sonner";
import { geo, activations, squaresApi } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LangContext";
import { buildGridLayer, ISRAEL_BOUNDS } from "../../lib/mapGrid";

function FitIsrael() {
  const map = useMap();
  useEffect(() => {
    map.fitBounds(ISRAEL_BOUNDS, { padding: [10, 10] });
  }, [map]);
  return null;
}

function GridLayer() {
  const map = useMap();
  useEffect(() => {
    const grid = buildGridLayer();
    grid.addTo(map);
    return () => grid.remove();
  }, [map]);
  return null;
}

function useZoom() {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());
  useEffect(() => {
    const h = () => setZoom(map.getZoom());
    map.on("zoomend", h);
    return () => map.off("zoomend", h);
  }, [map]);
  return zoom;
}

function SquaresLayer({ squares, activeDistrict }) {
  const map = useMap();
  const zoom = useZoom();
  const layerRef = useRef(null);

  useEffect(() => {
    if (layerRef.current) {
      layerRef.current.remove();
      layerRef.current = null;
    }
    if (!Array.isArray(squares) || !squares.length) return;
    // Labels need a minimum zoom to be readable; otherwise they overlap.
    if (zoom < 8) return;

    const group = L.layerGroup();
    const filtered =
      activeDistrict && activeDistrict !== "ALL"
        ? squares.filter(
            (s) => (s.district || s.district_code) === activeDistrict,
          )
        : squares;

    filtered.forEach((s) => {
      const lat = s.center_lat ?? s.lat ?? s.latitude;
      const lng = s.center_lon ?? s.center_lng ?? s.lng ?? s.longitude;
      const label =
        s.name || s.code || s.id || s.locator || s.square_code || "";
      if (typeof lat !== "number" || typeof lng !== "number" || !label) return;

      const icon = L.divIcon({
        className: "",
        html: `<div class="sq-label">${label}</div>`,
        iconSize: [0, 0],
      });
      L.marker([lat, lng], {
        icon,
        interactive: false,
        keyboard: false,
      }).addTo(group);
    });

    group.addTo(map);
    layerRef.current = group;
    return () => group.remove();
  }, [squares, activeDistrict, zoom, map]);

  return null;
}

const buildLocationIcon = () =>
  L.divIcon({
    className: "",
    html: `<div style="position:relative;width:22px;height:22px">
      <div style="position:absolute;inset:0;border-radius:50%;background:#007AFF;box-shadow:0 0 0 3px white,0 0 18px #007AFFcc;"></div>
    </div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });

export default function MapView() {
  const { user } = useAuth();
  const { t } = useLang();
  const [activeFilter, setActiveFilter] = useState("ALL");
  const [pos, setPos] = useState(null);
  const [checking, setChecking] = useState(false);

  // Three sources of truth — all from the production API
  const [districts, setDistricts] = useState([]); // [{code, name, color}]
  const [boundaries, setBoundaries] = useState(null); // GeoJSON FeatureCollection
  const [squares, setSquares] = useState([]); // [{code, center_lat, center_lon, district}]

  const geoJsonRef = useRef(null);

  // Color lookup: district code -> color
  const colorOf = (code) => {
    const d = districts.find((x) => x.code === code);
    return d?.color || "#888888";
  };
  const nameOf = (code) => {
    const d = districts.find((x) => x.code === code);
    return d?.name || code;
  };

  // 1. Load districts (colors + codes) from API
  useEffect(() => {
    geo
      .districts()
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.items || [];
        if (list.length) setDistricts(list);
      })
      .catch(() => {});
  }, []);

  // 2. Load boundaries (geometry) from API
  useEffect(() => {
    let cancelled = false;
    const normalize = (data) => {
      if (data?.type === "FeatureCollection") return data;
      if (Array.isArray(data?.features))
        return { type: "FeatureCollection", features: data.features };
      if (Array.isArray(data))
        return {
          type: "FeatureCollection",
          features: data.map((d, i) => ({
            type: "Feature",
            properties: {
              code: d.code || d.id || `D${i}`,
              name: d.name || d.title || d.code,
            },
            geometry: d.geometry || d.boundary,
          })),
        };
      return null;
    };

    geo
      .adminBoundaries()
      .then((data) => {
        if (cancelled) return;
        const fc = normalize(data);
        if (fc && fc.features?.length) setBoundaries(fc);
      })
      .catch(() => {
        /* API unreachable — leave empty */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // 3. Load squares (with center_lat/center_lon for labels) from API
  useEffect(() => {
    squaresApi
      .list()
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.items || [];
        if (list.length) setSquares(list);
      })
      .catch(() => {});
  }, []);

  // Re-style polygons when filter changes
  useEffect(() => {
    if (!geoJsonRef.current) return;
    geoJsonRef.current.eachLayer((l) => {
      const code = l.feature?.properties?.code;
      const isOn = activeFilter === "ALL" || activeFilter === code;
      l.setStyle({
        fillOpacity: isOn ? 0.45 : 0.08,
        opacity: isOn ? 1 : 0.35,
        weight: 2,
      });
    });
  }, [activeFilter]);

  const styleFeature = (feature) => {
    const code = feature?.properties?.code;
    const color = colorOf(code);
    return {
      color,
      weight: 2,
      opacity: 1,
      fillColor: color,
      fillOpacity: 0.45,
      smoothFactor: 1,
      lineJoin: "round",
      lineCap: "round",
    };
  };

  const onEachFeature = (feature, layer) => {
    const p = feature?.properties || {};
    const code = p.code;
    const color = colorOf(code);
    const name = nameOf(code) || p.name || "";
    layer.bindPopup(
      `<div style="min-width:140px">
        <div style="display:inline-block;padding:2px 8px;border-radius:6px;background:${color};color:#fff;font-weight:700;font-size:11px;margin-bottom:4px">${code || ""}</div>
        <div style="font-weight:600;font-size:13px;color:#fff">${name}</div>
      </div>`,
    );
  };

  const locateMe = () => {
    if (!navigator.geolocation) {
      toast.error("Геолокация не поддерживается");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setPos({ lat: p.coords.latitude, lng: p.coords.longitude });
        toast.success("Координаты получены");
      },
      () => toast.error("Не удалось получить координаты"),
    );
  };

  const checkIn = async () => {
    if (!user) {
      toast.error("Войдите, чтобы выполнить check-in");
      return;
    }
    if (!pos) {
      toast.error("Сначала нажмите кнопку геолокации");
      return;
    }
    setChecking(true);
    try {
      await activations.bulkSync([
        {
          lat: pos.lat,
          lng: pos.lng,
          timestamp: new Date().toISOString(),
          source: "web",
        },
      ]);
      toast.success("Check-in отправлен!");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          err?.message ||
          "Не удалось отправить check-in",
      );
    } finally {
      setChecking(false);
    }
  };

  // Build filter chips from /districts (real codes/colors/names)
  const filterChips =
    districts.length > 0
      ? districts
      : (boundaries?.features || []).map((f) => ({
          code: f.properties?.code,
          name: f.properties?.name,
          color: "#888888",
        }));

  return (
    <div
      className="relative h-[calc(100vh-128px)] w-full overflow-hidden"
      data-testid="map-view"
    >
      <MapContainer
        center={[31.5, 34.95]}
        zoom={8}
        scrollWheelZoom
        style={{ width: "100%", height: "100%" }}
        zoomControl={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; OpenStreetMap'
        />
        <FitIsrael />
        <GridLayer />
        {boundaries && districts.length >= 0 && (
          <GeoJSON
            key={`bnd-${boundaries.features.length}-${districts.length}`}
            ref={geoJsonRef}
            data={boundaries}
            style={styleFeature}
            onEachFeature={onEachFeature}
          />
        )}
        <SquaresLayer squares={squares} activeDistrict={activeFilter} />
        {pos && (
          <>
            <Marker position={[pos.lat, pos.lng]} icon={buildLocationIcon()} />
            <Circle
              center={[pos.lat, pos.lng]}
              radius={500}
              pathOptions={{
                color: "#007AFF",
                fillColor: "#007AFF",
                fillOpacity: 0.1,
              }}
            />
          </>
        )}
      </MapContainer>

      {/* Top floating stats card */}
      <div className="absolute top-3 left-3 right-3 z-[400]">
        <div
          data-testid="map-stats-card"
          className="ios-card-elevated p-4 flex items-center justify-between"
        >
          <div>
            <p className="text-[10px] text-white/50 uppercase tracking-wider font-semibold">
              HolyLand Award
            </p>
            <p className="text-2xl font-display font-bold text-white">
              {user?.score ?? 0}{" "}
              <span className="text-sm text-white/40 font-normal">
                / {user?.target ?? (squares.length || 503)}
              </span>
            </p>
          </div>
          <Badge className="bg-[#FF453A]/15 text-[#FF453A] border-[#FF453A]/25 text-base font-bold px-3 py-1">
            {user?.percent ?? "0%"}
          </Badge>
        </div>
      </div>

      {/* District filter chips */}
      <div className="absolute top-24 left-0 right-0 z-[400] overflow-x-auto px-3 pb-2 no-scrollbar">
        <div className="flex gap-2 w-max">
          <button
            data-testid="filter-all-btn"
            onClick={() => setActiveFilter("ALL")}
            className={`px-4 py-2 rounded-full text-xs font-semibold border whitespace-nowrap transition-all ${
              activeFilter === "ALL"
                ? "bg-white text-[#0A0A0A] border-white"
                : "bg-[#1c1c1e]/85 backdrop-blur text-white border-white/10"
            }`}
          >
            {t("app.all_districts")}
          </button>
          {filterChips.map((d) => (
            <button
              key={d.code}
              data-testid={`filter-${d.code}-btn`}
              onClick={() => setActiveFilter(d.code)}
              className={`px-4 py-2 rounded-full text-xs font-semibold border whitespace-nowrap transition-all flex items-center gap-2 ${
                activeFilter === d.code
                  ? "bg-white text-[#0A0A0A] border-white"
                  : "bg-[#1c1c1e]/85 backdrop-blur text-white border-white/10"
              }`}
            >
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: d.color }}
              />
              {d.code} · {d.name}
            </button>
          ))}
        </div>
      </div>

      {/* Action buttons */}
      <div className="absolute bottom-4 right-4 z-[400] flex flex-col gap-3 items-end">
        <Button
          data-testid="checkin-btn"
          onClick={checkIn}
          disabled={checking}
          className="bg-[#FF6B35] hover:bg-[#FF855A] text-white rounded-full px-5 h-12 font-semibold glow-orange"
        >
          {checking ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <MapPin className="h-4 w-4 mr-2" />
          )}
          {t("app.checkin")}
        </Button>
        <Button
          data-testid="locate-me-btn"
          onClick={locateMe}
          className="bg-[#007AFF] hover:bg-[#0A84FF] text-white rounded-full h-12 w-12 p-0 glow-blue"
        >
          <Navigation className="h-5 w-5" />
        </Button>
      </div>

      {!user && (
        <div
          data-testid="map-login-hint"
          className="absolute bottom-24 left-3 right-3 z-[400]"
        >
          <div className="ios-card-elevated p-3 px-4 flex items-center gap-3 text-sm">
            <Award className="h-4 w-4 text-[#FF6B35] shrink-0" />
            <span className="text-white/75">{t("app.login_hint")}</span>
          </div>
        </div>
      )}
    </div>
  );
}
